import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import { AdjustmentWhyFields, adjustmentWhyPayload, adjustmentWhyProblem, type AdjustmentWhyValue } from '@shared-ui/components';

interface ScoreOverridePanelProps {
    submissionId: string;
}

export function ScoreOverridePanel({ submissionId }: ScoreOverridePanelProps) {
    const queryClient = useQueryClient();
    const [correctness, setCorrectness] = useState<number | ''>('');
    const [approach, setApproach] = useState<number | ''>('');
    const [speed, setSpeed] = useState<number | ''>('');
    const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
    // An override replaces the machine's verdict with a person's: whose side the
    // problem was on and what exactly are required. See analysis-adjustments-target.md §4d.
    const [why, setWhy] = useState<AdjustmentWhyValue>({});
    const whyProblem = adjustmentWhyProblem(why, { origin: true, reasonCode: true });

    const mutation = useMutation({
        mutationFn: (scores: { correctnessScore?: number; approachScore?: number; speedScore?: number }) =>
            adminApi.overrideScores(submissionId, { ...scores, ...adjustmentWhyPayload(why) }),
        onMutate: () => setStatus('idle'),
        onSuccess: () => {
            setStatus('saved');
            queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
            queryClient.invalidateQueries({ queryKey: ['modelRuns', submissionId] });
            setCorrectness('');
            setApproach('');
            setSpeed('');
            setWhy({});
            setTimeout(() => setStatus('idle'), 2000);
        },
        onError: () => {
            setStatus('error');
            setTimeout(() => setStatus('idle'), 3000);
        },
    });

    const noChanges = correctness === '' && approach === '' && speed === '';

    return (
        <div className="score-override-section">
            <h4>Score Override</h4>
            <p className="field-desc">Override individual scores. Automatically creates eval ground truth from your corrections.</p>
            <div className="override-fields">
                <div className="override-field">
                    <label>Correctness (0-10)</label>
                    <input type="number" min="0" max="10" value={correctness}
                        onChange={e => setCorrectness(e.target.value ? parseInt(e.target.value) : '')}
                        placeholder="—" />
                </div>
                <div className="override-field">
                    <label>Approach (0-10)</label>
                    <input type="number" min="0" max="10" value={approach}
                        onChange={e => setApproach(e.target.value ? parseInt(e.target.value) : '')}
                        placeholder="—" />
                </div>
                <div className="override-field">
                    <label>Speed (0-10)</label>
                    <input type="number" min="0" max="10" value={speed}
                        onChange={e => setSpeed(e.target.value ? parseInt(e.target.value) : '')}
                        placeholder="—" />
                </div>
                <div className="override-field override-action">
                    <button
                        className="save-btn"
                        onClick={() => mutation.mutate({
                            correctnessScore: correctness !== '' ? correctness : undefined,
                            approachScore: approach !== '' ? approach : undefined,
                            speedScore: speed !== '' ? speed : undefined,
                        })}
                        disabled={mutation.isPending || noChanges || !!whyProblem}
                        title={whyProblem ?? undefined}
                    >
                        {mutation.isPending ? 'Saving...' : 'Override'}
                    </button>
                    {status === 'saved' && <span className="status saved">Saved!</span>}
                    {status === 'error' && <span className="status error">{mutation.error instanceof Error ? mutation.error.message : 'Failed'}</span>}
                </div>
            </div>
            <AdjustmentWhyFields value={why} onChange={setWhy} originRequired reasonRequired disabled={mutation.isPending} />
            {mutation.isSuccess && mutation.data && (
                <div className="override-diff">
                    {Object.entries(mutation.data.scoreDiff).map(([key, diff]) => (
                        diff.old !== diff.new ? (
                            <span key={key} className="diff-item">
                                {key}: {diff.old ?? '—'} → {diff.new ?? '—'}
                            </span>
                        ) : null
                    ))}
                </div>
            )}
        </div>
    );
}
